import { BLOCK_SUBJECTS, DAYS, FRANJAS, groupLabel } from "../lib/api";

const EmptyCell = ({ start, end, day }) => (
  <div
    data-testid={`slot-empty-${day}-${start}`}
    className="h-full rounded-xl border border-dashed border-border bg-muted/40 p-3 text-center text-sm text-ink-700 opacity-60"
  >
    Sin clase de {start} a {end}
  </div>
);

export const SlotCard = ({ slot, selected, onClick, disabled, eligible = true, enrolled = false }) => {
  const blocked = slot.full || !eligible;
  const clickable = onClick && !blocked && !disabled;
  const badge = !eligible
    ? "No corresponde a su etapa"
    : slot.full
      ? "Completo"
      : slot.pending_min
        ? "Disponible · pendiente de confirmar"
        : "Disponible";
  return (
    <button
      type="button"
      data-testid={`slot-${slot.id}`}
      onClick={clickable ? () => onClick(slot) : undefined}
      disabled={!clickable}
      className={`h-full w-full rounded-xl border-2 p-3 text-left transition-colors ${
        selected || enrolled
          ? "border-centro bg-centro-light"
          : blocked
            ? "cursor-not-allowed border-border bg-muted text-ink-800 opacity-75"
            : clickable
              ? "border-border bg-white hover:border-centro"
              : "border-border bg-white opacity-80"
      }`}
    >
      {enrolled && (
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-centro-text"
          data-testid={`slot-enrolled-${slot.id}`}>
          Su clase
        </p>
      )}
      <p className="font-head text-base font-medium">{slot.label || groupLabel(slot.stage_group)}</p>
      {slot.block && (
        <p className="mt-1 inline-block rounded-full bg-centro-light px-2 py-0.5 text-xs font-semibold text-centro-text">
          {slot.block}
        </p>
      )}
      {slot.block && BLOCK_SUBJECTS[slot.block] && (
        <p className="mt-1 text-xs text-ink-700" data-testid={`slot-subjects-${slot.id}`}>
          {BLOCK_SUBJECTS[slot.block].join(" · ")}
        </p>
      )}
      {slot.kind === "gr" && (
        <p className="mt-1 text-sm text-ink-700">Grupo Reducido · 15 € por sesión</p>
      )}
      <p className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
        slot.full ? "bg-red-100 text-red-800"
          : slot.pending_min ? "bg-amber-100 text-amber-900" : "bg-logo-light text-logo-text"}`}>
        {badge}
      </p>    </button>
  );
};

/**
 * Cuadrícula completa de lunes a viernes con todas las franjas de una hora.
 * Nunca se oculta ninguna franja: las que no corresponden aparecen atenuadas.
 */
export const WeekGrid = ({ slots, selectedIds = [], enrolledIds = [], onSelect, disabled, isEligible }) => {
  // Una franja recoge cualquier clase que empiece dentro de esa hora, aunque el horario
  // guardado tenga minutos antiguos (16:20, 18:20, 20:20…).
  const inFranja = (slot, day, start, end) =>
    slot.day === day && slot.start >= start && slot.start < end;
  const cellProps = (slot) => ({
    slot,
    selected: selectedIds.includes(slot.id),
    enrolled: enrolledIds.includes(slot.id),
    onClick: onSelect,
    disabled,
    eligible: isEligible ? isEligible(slot) : true,
  });
  return (
    <>
      {/* Vista apilada por día en pantallas pequeñas */}
      <div className="space-y-8 lg:hidden" data-testid="week-list">
        {[1, 2, 3, 4, 5].map((d) => (
          <div key={d}>
            <h3 className="mb-3 font-head text-xl font-medium">{DAYS[d]}</h3>
            <div className="space-y-3">
              {FRANJAS.map(([start, end]) => {
                const cell = slots.filter((s) => inFranja(s, d, start, end));
                return (
                  <div key={start}>
                    <p className="mb-1 font-head text-base font-medium text-ink-800">
                      {start}–{end}
                    </p>
                    {cell.length > 0
                      ? cell.map((slot) => <SlotCard key={slot.id} {...cellProps(slot)} />)
                      : <EmptyCell start={start} end={end} day={d} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Cuadrícula semanal completa en pantallas grandes */}
      <div className="hidden lg:block" data-testid="week-grid">
        <table className="w-full border-separate border-spacing-2">
          <thead>
            <tr>
              <th className="w-28 text-left text-base font-medium text-ink-700">Franja</th>
              {[1, 2, 3, 4, 5].map((d) => (
                <th key={d} className="font-head text-lg font-medium">{DAYS[d]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FRANJAS.map(([start, end]) => (
              <tr key={start}>
                <th className="align-top text-left font-head text-base font-medium text-ink-800">
                  {start}–{end}
                </th>
                {[1, 2, 3, 4, 5].map((d) => {
                  const cell = slots.filter((s) => inFranja(s, d, start, end));
                  return (
                    <td key={d} className="align-top">
                      {cell.length > 0 ? (
                        <div className="space-y-2">
                          {cell.map((slot) => <SlotCard key={slot.id} {...cellProps(slot)} />)}
                        </div>
                      ) : (
                        <EmptyCell start={start} end={end} day={d} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
